import { validateMemberEmail } from './timecard-member-email.js';

// Only a short-lived UI intent is stored. Never store email, tokens, or consent here.
const MEMBER_INTENT = 'timecard:member:next-step';
const MEMBER_RETURN = 'timecard_member_return';
function rememberNextStep(kind) {
  try { sessionStorage.setItem(MEMBER_INTENT, JSON.stringify({ kind, at: Date.now() })); } catch {}
}

// Carry UI intent in the return URL too: storage is not shared with Shop's popup.
// This flag is presentation only; Liquid/Shopify still determines signed-in/member status.
function membershipLoginUrl(href) {
  const url = new URL(href, window.location.origin);
  if (url.origin !== window.location.origin || url.pathname !== '/customer_authentication/login') return null;
  const destination = new URL(window.location.href);
  destination.searchParams.set(MEMBER_RETURN, '1');
  destination.hash = '';
  url.searchParams.set('return_to', destination.pathname + destination.search);
  url.searchParams.delete('ui_hint');
  return url;
}

// This module only hands off to Shopify. It cannot verify codes or grant benefits.
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-member-signin]')) return;
  event.preventDefault();
  const input = form.querySelector('input[type=email]');
  const error = form.querySelector('[data-member-error]');
  if (!(input instanceof HTMLInputElement) || !error) return;
  const result = validateMemberEmail(input.value);
  input.value = result.email;
  input.setAttribute('aria-invalid', String(!result.valid));
  error.textContent = result.error;
  error.hidden = result.valid;
  if (!result.valid) { input.focus(); return; }
  const url = membershipLoginUrl(form.action);
  // The action is rendered by Shopify's documented route, never taken from user input.
  if (!url) {
    error.textContent = 'Sign-in is temporarily unavailable. Please use the account link.';
    error.hidden = false;
    return;
  }
  url.searchParams.set('login_hint', result.email);
  url.searchParams.set('login_hint_mode', 'submit');
  url.searchParams.delete('ui_hint');
  rememberNextStep('signin');
  // Do not persist the email or consent locally.
  window.location.assign(url.href);
});
document.addEventListener('click', (event) => {
  const link = event.target.closest?.('[data-member-provider]');
  if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const provider = link.dataset.memberProvider;
  if (!['google', 'facebook'].includes(provider)) return;
  const url = membershipLoginUrl(link.href);
  if (!url) return;
  url.searchParams.delete('ui_hint');
  url.searchParams.set('acr_values', `provider:${provider}`);
  event.preventDefault();
  rememberNextStep('signin');
  window.location.assign(url.href);
});
document.addEventListener('click', (event) => {
  if (event.target instanceof Element && event.target.closest('.tc-membership__shop')) rememberNextStep('signin');
}, true);
document.addEventListener('input', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.closest('[data-member-signin]')) return;
  const error = input.form?.querySelector('[data-member-error]');
  input.setAttribute('aria-invalid', 'false');
  if (error) { error.hidden = true; error.textContent = ''; }
});

function openMembership() {
  const modal = document.querySelector('[data-timecard-newsletter-modal]');
  if (!modal) return false;
  customElements.whenDefined(modal.localName).then(() => {
    if (!document.querySelector('dialog[open]') && typeof modal.showDialog === 'function') modal.showDialog();
  });
  return true;
}

document.addEventListener('click', (event) => {
  const link = event.target.closest?.('[data-member-open]');
  if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (openMembership()) event.preventDefault();
});

function validateConsent(form) {
  let firstInvalid;
  for (const input of form.querySelectorAll('[data-member-consent-choice]')) {
    const error = document.getElementById(input.getAttribute('aria-describedby'));
    const invalid = !input.checked;
    input.setAttribute('aria-invalid', String(invalid));
    if (error) error.hidden = !invalid;
    if (invalid && !firstInvalid) firstInvalid = input;
  }
  if (firstInvalid) { firstInvalid.focus(); return false; }
  return form.reportValidity();
}

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.matches('[data-member-consent-choice]')) return;
  const error = document.getElementById(input.getAttribute('aria-describedby'));
  if (input.checked) {
    input.setAttribute('aria-invalid', 'false');
    if (error) error.hidden = true;
  }
});

// Keep Shopify's native form submission and anti-bot handling intact.
// This is UI navigation intent only; it never grants or stores marketing consent.
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('.tc-membership__consent-form')) return;
  if (!validateConsent(form)) { event.preventDefault(); return; }
  rememberNextStep('consent');
});

function offerMembershipAfterReturn() {
  const modal = document.querySelector('[data-timecard-newsletter-modal]');
  const card = modal?.querySelector('.tc-membership');
  if (!modal) return;
  if (window.location.hash === '#timecard-email-membership' || window.location.hash.startsWith('#MemberConsent-')) {
    openMembership();
    return;
  }
  if (card?.dataset.memberSignedIn !== 'true') return;
  const current = new URL(window.location.href);
  const returned = current.searchParams.get(MEMBER_RETURN) === '1';
  if (returned) {
    current.searchParams.delete(MEMBER_RETURN);
    window.history.replaceState(window.history.state, '', current.pathname + current.search + current.hash);
  }
  let intent;
  try {
    intent = JSON.parse(sessionStorage.getItem(MEMBER_INTENT) || 'null');
    sessionStorage.removeItem(MEMBER_INTENT);
  } catch {}
  if (returned) {
    if (card.dataset.memberSubscribed !== 'true') openMembership();
    return;
  }
  if (!intent || !['signin', 'consent'].includes(intent.kind) || Date.now() - intent.at > 20 * 60 * 1000) return;
  // Never re-prompt an existing subscriber merely for signing in.
  if (intent.kind === 'signin' && card.dataset.memberSubscribed === 'true') return;
  openMembership();
}

function initializeMembership() {
  // Keep native required validation as the no-JavaScript fallback.
  document.querySelectorAll('.tc-membership__consent-form').forEach((form) => { form.noValidate = true; });
  document.querySelectorAll('.tc-membership__shop shop-login-button').forEach((button) => {
    if (button.dataset.memberReturnBound) return;
    button.dataset.memberReturnBound = 'true';
    // Shopify emits completed after its own cookie exchange. Do not handle tokens,
    // popup messages, or grant consent. Complete the normal storefront login handoff.
    button.addEventListener('completed', () => {
      // Payload fields differ between Shop flows. Completion only initiates the
      // native login route; Shopify independently verifies the actual session.
      const form = button.closest('.tc-membership')?.querySelector('[data-member-signin]');
      const url = form && membershipLoginUrl(form.action);
      if (!url || button.dataset.memberRedirecting) return;
      button.dataset.memberRedirecting = 'true';
      rememberNextStep('signin');
      window.location.assign(url.href);
    });
  });
  offerMembershipAfterReturn();
}
window.addEventListener('hashchange', () => {
  if (window.location.hash === '#timecard-email-membership') openMembership();
});
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeMembership, { once:true });
else initializeMembership();
window.addEventListener('pageshow', (event) => { if (event.persisted) initializeMembership(); });
document.addEventListener('shopify:section:load', initializeMembership);
